import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Loader2, Plus } from "lucide-react";
import { api, extractErrorMessage } from "@/api/client";
import { useZakazkaDetail } from "@/api/hooks/useZakazky";
import { queryKeys } from "@/api/queryKeys";
import type { DataResponse, Varianta, ZakazkaDetail } from "@/api/schemas";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { VyberZakazky, type ZakazkaNaPripojenie } from "@/components/zakazka/PripojitDialog";
import { formatEur, sklonuj } from "@/lib/format";
import { jeTovarovaVarianta } from "@/lib/variantyZakazky";
import { cn } from "@/lib/utils";

/** Kam regál pôjde — rozpočet tovaru zákazky. */
export type CielRegalu = { zakazkaId: string; variantaId: string; zakazka: string };

/** Krok okna — výber riadi okno, vkladanie a výsledok stránka Regály. */
export type StavVlozenia =
  | { krok: "vyber" }
  | { krok: "vkladam"; ciel: CielRegalu }
  | { krok: "hotovo"; ciel: CielRegalu; veta: string }
  | { krok: "chyba"; ciel: CielRegalu | null; veta: string };

export type RegalNaVlozenie = { nazov: string; zostav: number; spolu: number | null };

/** „Tovar“, „Tovar 2“… — ako ⋮ Nový tovar v editore zákazky. */
export function nazovNovehoTovaru(varianty: { nazov: string }[]): string {
  const obsadene = new Set(varianty.map((v) => v.nazov.toLowerCase()));
  let nazov = "Tovar";
  for (let i = 2; obsadene.has(nazov.toLowerCase()); i += 1) nazov = `Tovar ${i}`;
  return nazov;
}

const popisZakazky = (z: { cislo_zakazky: string; nazov?: string | null }): string =>
  [z.cislo_zakazky, z.nazov].filter(Boolean).join(" · ");

async function novyTovar(zakazkaId: string, nazov: string): Promise<string> {
  const { data } = await api.post<DataResponse<Varianta>>(`/zakazky/${zakazkaId}/varianty`, {
    nazov,
    odbor: "tovar",
  });
  return data.data.id;
}

/**
 * Vložiť do zákazky… zo stránky Regály (KOLO 237). User si vybral „konfigurátor +
 * Vložiť do zákazky“: regál nakreslený v hornom menu ide do rozpočtu tovaru
 * vybranej zákazky — existujúceho bez regálu alebo nového. Rozpočet tovaru s regálom
 * sa nenahrádza (zostavy iného regálu by zmizli) — ten sa upravuje v zákazke.
 */
export function VlozitDoZakazkyDialog({
  regal,
  stav,
  onVyber,
  onOtvor,
  onSpat,
  onClose,
}: {
  regal: RegalNaVlozenie;
  stav: StavVlozenia;
  onVyber: (ciel: CielRegalu) => void;
  onOtvor: (ciel: CielRegalu) => void;
  onSpat: () => void;
  onClose: () => void;
}): JSX.Element {
  const zostav = `${regal.zostav} ${sklonuj(regal.zostav, ["zostava", "zostavy", "zostáv"])}`;
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        // počas vkladania okno nezavrieť — stránka čaká na odpoveď
        if (!o && stav.krok !== "vkladam") onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Vložiť regál do zákazky</DialogTitle>
          <DialogDescription>
            {regal.nazov || "Regál CRC"} · {zostav}
            {regal.spolu != null ? ` · ${formatEur(regal.spolu)} bez DPH` : ""} — zostavy pôjdu do
            rozpočtu tovaru, diely Tandemu rozpočtárovi na nacenenie.
          </DialogDescription>
        </DialogHeader>
        {stav.krok === "vyber" ? (
          <Vyber regal={regal} onVyber={onVyber} onClose={onClose} />
        ) : stav.krok === "vkladam" ? (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground" role="status">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Vkladám regál do {stav.ciel.zakazka}…
          </p>
        ) : stav.krok === "hotovo" ? (
          <>
            <div className="flex gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <p>
                <span className="font-semibold">{stav.ciel.zakazka}</span> — {stav.veta}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Zavrieť
              </Button>
              <Button onClick={() => onOtvor(stav.ciel)}>Otvoriť zákazku</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              Regál sa nevložil: {stav.veta}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Zavrieť
              </Button>
              <Button onClick={onSpat}>Späť na výber</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Vyber({
  regal,
  onVyber,
  onClose,
}: {
  regal: RegalNaVlozenie;
  onVyber: (ciel: CielRegalu) => void;
  onClose: () => void;
}): JSX.Element {
  const [nova, setNova] = useState(false);
  const [zakazka, setZakazka] = useState<ZakazkaNaPripojenie | null>(null);
  return (
    <div className="space-y-3">
      <div className="flex gap-1 rounded-md bg-muted p-1 text-sm" role="tablist">
        {(
          [
            [false, "Existujúca zákazka"],
            [true, "Nová zákazka"],
          ] as const
        ).map(([hodnota, text]) => (
          <button
            key={text}
            type="button"
            role="tab"
            aria-selected={nova === hodnota}
            onClick={() => setNova(hodnota)}
            className={cn(
              "flex-1 rounded px-3 py-1.5 font-medium",
              nova === hodnota ? "bg-background shadow-sm" : "text-muted-foreground",
            )}
          >
            {text}
          </button>
        ))}
      </div>
      {nova ? (
        <NovaZakazka regal={regal} onVyber={onVyber} onClose={onClose} />
      ) : zakazka ? (
        <VyberTovaru zakazka={zakazka} onZmen={() => setZakazka(null)} onVyber={onVyber} onClose={onClose} />
      ) : (
        <VyberZakazky onVyber={setZakazka} />
      )}
    </div>
  );
}

function NovaZakazka({
  regal,
  onVyber,
  onClose,
}: {
  regal: RegalNaVlozenie;
  onVyber: (ciel: CielRegalu) => void;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [nazov, setNazov] = useState(regal.nazov || "");
  const [chyba, setChyba] = useState<string | null>(null);
  const [cakam, setCakam] = useState(false);

  async function zaloz(): Promise<void> {
    setCakam(true);
    setChyba(null);
    try {
      // bez čísla = výpočet V (KOLO 200) — číslo AA sa doplní neskôr v zákazke
      const { data } = await api.post<DataResponse<ZakazkaDetail>>("/zakazky", {
        nazov: nazov.trim() || "Regál CRC",
      });
      const z = data.data;
      const variantaId = await novyTovar(z.id, "Tovar");
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
      onVyber({ zakazkaId: z.id, variantaId, zakazka: popisZakazky(z) });
    } catch (err) {
      setChyba(extractErrorMessage(err));
      setCakam(false);
    }
  }

  return (
    <div className="space-y-3">
      <label className="block space-y-1 text-sm">
        <span className="font-medium">Názov zákazky</span>
        <Input autoFocus value={nazov} onChange={(e) => setNazov(e.target.value)} placeholder="Regál CRC" />
      </label>
      <p className="text-xs text-muted-foreground">
        Zákazka sa založí ako výpočet (V…) s rozpočtom tovaru „Tovar“. Číslo AA doplníš v zákazke.
      </p>
      {chyba ? <p className="text-sm text-destructive">{chyba}</p> : null}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Zrušiť
        </Button>
        <Button onClick={() => void zaloz()} disabled={cakam}>
          {cakam ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden /> : null}
          Založiť a vložiť
        </Button>
      </DialogFooter>
    </div>
  );
}

const NOVY = "novy";

function VyberTovaru({
  zakazka,
  onZmen,
  onVyber,
  onClose,
}: {
  zakazka: ZakazkaNaPripojenie;
  onZmen: () => void;
  onVyber: (ciel: CielRegalu) => void;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const detail = useZakazkaDetail(zakazka.id);
  const varianty = useMemo(() => detail.data?.data.varianty ?? [], [detail.data]);
  const tovary = varianty.filter(jeTovarovaVarianta);
  const volny = tovary.find((v) => !v.ma_regal);
  const [ciel, setCiel] = useState<string | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const [cakam, setCakam] = useState(false);
  // predvolený je prvý rozpočet tovaru bez regálu, inak nový
  useEffect(() => {
    if (detail.data && ciel === null) setCiel(volny?.id ?? NOVY);
  }, [detail.data, ciel, volny]);
  const novyNazov = nazovNovehoTovaru(varianty);

  async function vloz(): Promise<void> {
    if (!ciel) return;
    setCakam(true);
    setChyba(null);
    try {
      const variantaId = ciel === NOVY ? await novyTovar(zakazka.id, novyNazov) : ciel;
      if (ciel === NOVY) void qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazka.id) });
      onVyber({ zakazkaId: zakazka.id, variantaId, zakazka: popisZakazky(zakazka) });
    } catch (err) {
      setChyba(extractErrorMessage(err));
      setCakam(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={onZmen}>
          <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
          Iná zákazka
        </Button>
        <span className="min-w-0 truncate font-medium">{popisZakazky(zakazka)}</span>
      </div>
      {detail.isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : detail.isError ? (
        <p className="text-sm text-destructive">{extractErrorMessage(detail.error)}</p>
      ) : (
        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-sm font-medium">Rozpočet tovaru</legend>
          {tovary.map((v) => (
            <label
              key={v.id}
              className={cn(
                "flex items-start gap-2 rounded-md border px-3 py-2 text-sm",
                v.ma_regal ? "cursor-not-allowed opacity-60" : "cursor-pointer",
              )}
            >
              <input
                type="radio"
                name="ciel-regalu"
                className="mt-0.5"
                disabled={v.ma_regal}
                checked={ciel === v.id}
                onChange={() => setCiel(v.id)}
              />
              <span>
                <span className="font-medium">{v.nazov}</span>
                {v.ma_regal ? (
                  <span className="block text-xs text-muted-foreground">
                    Má regál — uprav ho v zákazke (Upraviť regál).
                  </span>
                ) : null}
              </span>
            </label>
          ))}
          <label className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm">
            <input
              type="radio"
              name="ciel-regalu"
              checked={ciel === NOVY}
              onChange={() => setCiel(NOVY)}
            />
            <Plus className="h-4 w-4 text-muted-foreground" aria-hidden />
            <span>
              Nový rozpočet tovaru <span className="text-muted-foreground">({novyNazov})</span>
            </span>
          </label>
        </fieldset>
      )}
      {chyba ? <p className="text-sm text-destructive">{chyba}</p> : null}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Zrušiť
        </Button>
        <Button onClick={() => void vloz()} disabled={!ciel || cakam || !detail.data}>
          {cakam ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden /> : null}
          Vložiť regál
        </Button>
      </DialogFooter>
    </div>
  );
}
